import { useState } from 'react';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { useAuth } from '@/hooks/use-auth';
import { StripeConnectOnboarding } from '@/components/StripeConnectOnboarding';
import { Separator } from '@/components/ui/separator';
import Layout from '@/components/Layout';

export default function SellerSettings() {
  const { user } = useAuth();
  const [activeTab, setActiveTab] = useState('payment');

  if (!user) {
    return <div>Please log in to access settings.</div>;
  }

  return (
    <Layout>
      <div className="container py-10">
        <div className="mb-8">
          <h1 className="text-3xl font-bold tracking-tight">Seller Settings</h1>
          <p className="text-muted-foreground mt-2">
            Manage your seller profile, payment methods, and preferences.
          </p>
        </div>

        <Tabs defaultValue={activeTab} onValueChange={setActiveTab} className="space-y-4">
          <TabsList>
            <TabsTrigger value="payment">Payment</TabsTrigger>
            <TabsTrigger value="profile">Profile</TabsTrigger>
            <TabsTrigger value="preferences">Preferences</TabsTrigger>
          </TabsList>

          <TabsContent value="payment" className="space-y-6">
            <Card>
              <CardHeader>
                <CardTitle>Payment Settings</CardTitle>
                <CardDescription>
                  Connect your Stripe account to receive payments from buyers.
                </CardDescription>
              </CardHeader>
              <CardContent>
                <StripeConnectOnboarding />
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle>Payout Settings</CardTitle>
                <CardDescription>
                  Manage how and when you receive your payments.
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="text-sm text-muted-foreground">
                  Payout settings are managed through your Stripe Connect account. 
                  Please complete your Stripe Connect setup to access payout options.
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle>Transaction History</CardTitle>
                <CardDescription>
                  View your past transactions and payouts.
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="text-sm">
                  No transactions found. Once you start receiving payments, they will appear here.
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="profile" className="space-y-6">
            <Card>
              <CardHeader>
                <CardTitle>Seller Profile</CardTitle>
                <CardDescription>
                  Manage your public seller profile information.
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-2">
                  <div className="text-sm text-muted-foreground">
                    Edit your seller profile in the seller dashboard.
                  </div>
                  <Separator className="my-4" />
                  <div className="flex justify-end">
                    <a
                      href="/dashboard/seller"
                      className="text-sm text-primary hover:underline"
                    >
                      Go to Seller Dashboard
                    </a>
                  </div>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="preferences" className="space-y-6">
            <Card>
              <CardHeader>
                <CardTitle>Communication Preferences</CardTitle>
                <CardDescription>
                  Manage how you receive notifications and messages.
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="text-sm text-muted-foreground">
                  Communication preferences can be managed in your account settings.
                </div>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>
    </Layout>
  );
} 